import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { 
  Heart, ArrowLeft, Check, Sparkles, Crown, Loader2, Gift, Users, Ticket, Clock, AlertCircle, Zap, Tag
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [foundingMember, setFoundingMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [startingTrial, setStartingTrial] = useState(null);
  const [betaCode, setBetaCode] = useState("");
  const [redeemingBeta, setRedeemingBeta] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [redeemingCoupon, setRedeemingCoupon] = useState(false);

  useEffect(() => {
    fetchData();
    
    // Check if coming back from trial checkout
    if (searchParams.get('trial') === 'started') {
      toast.success("🎉 Your 3-day free trial has started! Enjoy full premium access.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, statusRes] = await Promise.all([
        axios.get(`${API}/subscription/plans`),
        axios.get(`${API}/subscription/status`, { 
          headers: { Authorization: `Bearer ${token}` } 
        })
      ]);
      setPlans(plansRes.data.plans);
      setFoundingMember(plansRes.data.founding_member);
      setSubscription(statusRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartTrial = async (planId) => {
    setStartingTrial(planId);
    try {
      const response = await axios.post(
        `${API}/subscription/start-trial`,
        { 
          plan_id: planId,
          success_url: window.location.origin + '/subscription',
          cancel_url: window.location.origin + '/subscription'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Unable to start trial. Please try again.";
      toast.error(message);
      console.error("Trial start error:", error);
    } finally {
      setStartingTrial(null);
    }
  };

  const handleSubscribe = async (planId) => {
    setProcessingPlan(planId);
    try {
      const response = await axios.post(
        `${API}/subscription/checkout`,
        { 
          plan_id: planId,
          origin_url: window.location.origin 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe Payment Link
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error("Unable to start checkout. Please try again.");
      console.error("Checkout error:", error);
    } finally {
      setProcessingPlan(null);
    }
  };

  const handleCancelTrial = async () => {
    if (!confirm("Are you sure you want to cancel your trial? You will lose premium access immediately.")) {
      return;
    }
    
    try {
      await axios.post(
        `${API}/subscription/cancel-trial`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Trial cancelled. You will not be charged.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to cancel trial. Please contact support.");
    }
  };

  const handleRedeemBeta = async () => {
    if (!betaCode.trim()) {
      toast.error("Please enter a beta code");
      return;
    }
    
    setRedeemingBeta(true);
    try {
      const response = await axios.post(
        `${API}/subscription/redeem-beta`,
        { code: betaCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setBetaCode("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid or expired beta code");
    } finally {
      setRedeemingBeta(false);
    }
  };

  const handleRedeemCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error("Please enter a coupon code");
      return;
    }
    
    setRedeemingCoupon(true);
    try {
      const response = await axios.post(
        `${API}/subscription/redeem-coupon`,
        { coupon_code: couponCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setCouponCode("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid or unavailable coupon code");
    } finally {
      setRedeemingCoupon(false);
    }
  };

  const formatTrialTime = (hours) => {
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = Math.round(hours % 24);
      return `${days} day${days > 1 ? 's' : ''} ${remainingHours > 0 ? `${remainingHours}h` : ''}`;
    }
    return `${Math.round(hours)} hours`;
  };

  const getPlanPrice = (plan) => {
    // For trial: show the price they'll pay after trial
    // Based on founder eligibility (first 100 paid subscribers)
    if (foundingMember?.is_eligible) {
      return plan.discounted_price; // 25% off for founders
    }
    return plan.price;
  };

  if (loading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
            <Crown className="w-8 h-8 text-primary" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient" data-testid="subscription-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-muted-foreground hover:text-foreground"
            data-testid="subscription-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-primary" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold">Premium</span>
          </div>
          <div className="w-24"></div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Crown className="w-10 h-10 text-primary" strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-4">
            Unlock Your Full Journey
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            Access all 365 days of transformative content, unlimited Spirit Chat AI, 
            and your complete path to authenticity.
          </p>
        </div>

        {/* Active Trial Banner */}
        {subscription?.is_trial && subscription?.trial_hours_remaining && (
          <Card className="glass-card rounded-2xl border-0 mb-10 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/20" data-testid="trial-active-banner">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Clock className="w-7 h-7 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-foreground">
                      ⏰ Trial Active
                    </h3>
                    <p className="text-muted-foreground">
                      <span className="font-semibold text-amber-600">{formatTrialTime(subscription.trial_hours_remaining)}</span> remaining until your card is charged
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">
                    Plan: {plans.find(p => p.id === subscription.trial_plan_id)?.name || 'Premium'}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelTrial}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="cancel-trial-btn"
                  >
                    Cancel Trial
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Price Drop Banner */}
        {!subscription?.is_premium && (
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-full text-sm shadow-md" data-testid="price-drop-banner">
              <Sparkles className="w-4 h-4" />
              <span>Price drop: was <s className="opacity-70">$15.55</s>/mo — now <strong>$5.55</strong>. More accessible than ever.</span>
            </div>
          </div>
        )}

        {/* Founding Member Banner */}
        {foundingMember?.is_eligible && !subscription?.is_premium && (
          <Card className="glass-card rounded-2xl border-0 mb-10 bg-gradient-to-r from-primary/10 to-accent/10 overflow-hidden" data-testid="founding-member-banner">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
                    <Gift className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-foreground">
                      🎉 Founding Member Discount!
                    </h3>
                    <p className="text-muted-foreground">
                      Get <span className="text-primary font-semibold">25% off</span> as one of our first 100 members
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-white/50 dark:bg-white/10 rounded-full px-4 py-2">
                  <Users className="w-4 h-4 text-primary" />
                  <span className="font-semibold text-foreground">
                    {foundingMember.spots_left} of {foundingMember.total_spots} spots left
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current Status - Premium */}
        {subscription?.is_premium && !subscription?.is_trial && (
          <Card className="glass-card rounded-2xl border-0 mb-10 bg-primary/5">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Check className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground">
                    {subscription.is_beta ? "🧪 Beta Tester!" : "You're Premium!"}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {subscription.plan_name} • {subscription.expires_at ? `Access until ${new Date(subscription.expires_at).toLocaleDateString()}` : 'Lifetime Access'}
                  </p>
                </div>
              </div>
              <Sparkles className="w-8 h-8 text-primary" />
            </CardContent>
          </Card>
        )}

        {/* 3-Day Trial CTA - Show to non-premium users who haven't used trial */}
        {!subscription?.is_premium && subscription?.can_start_trial && (
          <Card className="glass-card rounded-3xl border-0 mb-10 bg-gradient-to-r from-green-500/10 to-emerald-500/10 ring-2 ring-green-500/30" data-testid="trial-cta-section">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 bg-green-500/20 text-green-700 dark:text-green-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
                  <Zap className="w-4 h-4" />
                  Try Premium Free for 3 Days
                </div>
                <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
                  Start Your Free Trial Today
                </h2>
                <p className="text-muted-foreground max-w-lg mx-auto">
                  Get full access to all premium features. Your card will only be charged after 3 days. Cancel anytime before then.
                </p>
                {foundingMember?.is_eligible && (
                  <p className="text-sm text-primary mt-2 font-medium">
                    🎁 As one of the first {foundingMember.spots_left} subscribers, you'll get 25% off + a Founder Badge!
                  </p>
                )}
              </div>
              
              <div className="grid md:grid-cols-3 gap-4">
                {plans.map((plan) => (
                  <div key={plan.id} className="bg-white/50 dark:bg-white/5 rounded-2xl p-4 text-center">
                    <h3 className="font-heading font-semibold text-foreground mb-1">{plan.name}</h3>
                    <div className="text-2xl font-bold text-foreground mb-1">
                      ${getPlanPrice(plan)}
                      {plan.interval !== 'lifetime' && <span className="text-sm font-normal text-muted-foreground">/{plan.interval}</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">charged after 3 days</p>
                    <Button
                      onClick={() => handleStartTrial(plan.id)}
                      disabled={startingTrial !== null}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`start-trial-${plan.id}-btn`}
                    >
                      {startingTrial === plan.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        "Start Free Trial"
                      )}
                    </Button>
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-center text-muted-foreground mt-4">
                <AlertCircle className="w-3 h-3 inline mr-1" />
                Your card will be securely saved but not charged until your trial ends
              </p>
            </CardContent>
          </Card>
        )}

        {/* Already used trial message */}
        {!subscription?.is_premium && !subscription?.can_start_trial && (
          <Card className="glass-card rounded-2xl border-0 mb-10 bg-muted/30">
            <CardContent className="p-6 text-center">
              <p className="text-muted-foreground">
                You've already used your free trial. Choose a plan below to continue your journey.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Beta Code Section */}
        {!subscription?.is_premium && (
          <Card className="glass-card rounded-2xl border-0 mb-6" data-testid="beta-code-section">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                  <Ticket className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground">
                    Have a Beta Code?
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Enter your code for free early access
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Input
                  placeholder="Enter beta code..."
                  value={betaCode}
                  onChange={(e) => setBetaCode(e.target.value.toUpperCase())}
                  className="flex-1 rounded-xl bg-white/50 dark:bg-white/10 uppercase"
                  disabled={redeemingBeta}
                  data-testid="beta-code-input"
                />
                <Button
                  onClick={handleRedeemBeta}
                  disabled={redeemingBeta || !betaCode.trim()}
                  className="btn-secondary"
                  data-testid="redeem-beta-btn"
                >
                  {redeemingBeta ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Redeem"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Coupon Code Section */}
        {!subscription?.is_premium && (
          <Card className="glass-card rounded-2xl border-0 mb-10 bg-gradient-to-r from-pink-500/5 to-rose-500/5" data-testid="coupon-code-section">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
                  <Tag className="w-5 h-5 text-pink-500" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground">
                    Have a Coupon or Scholarship Code?
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Enter COMPASSION for 3 months free, or a SCHOLARSHIP code for lifetime access
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Input
                  placeholder="Enter coupon code..."
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  className="flex-1 rounded-xl bg-white/50 dark:bg-white/10 uppercase"
                  disabled={redeemingCoupon}
                  data-testid="coupon-code-input"
                />
                <Button
                  onClick={handleRedeemCoupon}
                  disabled={redeemingCoupon || !couponCode.trim()}
                  className="bg-pink-500 hover:bg-pink-600 text-white"
                  data-testid="redeem-coupon-btn"
                >
                  {redeemingCoupon ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Redeem"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pricing Cards - Full purchase (for those who prefer to skip trial) */}
        {!subscription?.is_premium && (
          <>
            <div className="text-center mb-6">
              <p className="text-sm text-muted-foreground">Or purchase directly without trial:</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {plans.map((plan) => (
                <Card 
                  key={plan.id}
                  className={`glass-card rounded-3xl border-0 overflow-hidden ${
                    plan.id === 'annual' ? 'ring-2 ring-primary' : ''
                  }`}
                  data-testid={`plan-${plan.id}`}
                >
                  {plan.id === 'annual' && (
                    <div className="bg-primary text-primary-foreground text-center py-2 text-sm font-medium">
                      Most Popular — 2 Months Free
                    </div>
                  )}
                  {plan.id === 'lifetime' && foundingMember?.is_eligible && (
                    <div className="bg-accent text-white text-center py-2 text-sm font-medium">
                      Founding Member Price
                    </div>
                  )}
                  <CardContent className="p-8">
                    <div className="text-center mb-6">
                      <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                        {plan.name}
                      </h3>
                      <div className="flex items-baseline justify-center gap-2">
                        {plan.show_discount && (
                          <span className="text-lg text-muted-foreground line-through">
                            ${plan.original_price}
                          </span>
                        )}
                        <span className="text-4xl font-heading font-bold text-foreground">
                          ${plan.display_price}
                        </span>
                        {plan.interval !== 'lifetime' && (
                          <span className="text-muted-foreground">/{plan.interval}</span>
                        )}
                      </div>
                      {plan.show_discount && (
                        <span className="inline-block mt-2 text-xs bg-primary/10 text-primary px-2 py-1 rounded-full font-medium">
                          25% Founding Member Discount
                        </span>
                      )}
                    </div>

                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={processingPlan !== null}
                      className={`w-full ${
                        plan.id === 'annual' ? 'btn-primary' : 'btn-secondary'
                      }`}
                      data-testid={`subscribe-${plan.id}-btn`}
                    >
                      {processingPlan === plan.id ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        `Choose ${plan.name.split(' ')[0]}`
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* Free Features */}
        <Card className="glass-card rounded-3xl border-0">
          <CardContent className="p-8 text-center">
            <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
              What's Free Forever
            </h3>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
              <span className="bg-muted px-3 py-1 rounded-full">State of Being Quiz</span>
              <span className="bg-muted px-3 py-1 rounded-full">Daily Affirmation</span>
              <span className="bg-muted px-3 py-1 rounded-full">Morning & Evening Mantras</span>
              <span className="bg-muted px-3 py-1 rounded-full">Mood Tracker & Streaks</span>
              <span className="bg-muted px-3 py-1 rounded-full">Loving Kindness Practice</span>
              <span className="bg-muted px-3 py-1 rounded-full">7 Shadow Self Sessions</span>
              <span className="bg-muted px-3 py-1 rounded-full">5 Ancestral Wisdom Sessions</span>
              <span className="bg-muted px-3 py-1 rounded-full">4 Mood Playlists</span>
            </div>
          </CardContent>
        </Card>

        {/* Trust Message */}
        <div className="text-center mt-12">
          <p className="font-handwritten text-xl text-primary">
            "Investing in yourself is the highest return investment you can make"
          </p>
          <p className="text-sm text-muted-foreground mt-4">
            Secure payment powered by Stripe • Cancel anytime
          </p>
        </div>
      </main>
    </div>
  );
};

export default SubscriptionPage;
